/**
 * <CancelPaymentPage> — étape 4/5 : régler les frais (page)
 *
 * Ce qu'on va prélever, sur quelle carte, et ce qui se passe si la banque demande une
 * authentification ou refuse. L'étape n'existe que lorsqu'il y a des frais.
 *
 * `paymentStatus` est l'état **métier** du prélèvement : il arrive en props, il ne se devine pas
 * ici. Le libellé du bouton reprend le montant déjà écrit — la page n'en calcule aucun.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import {
    type UICancelPaymentMethod,
    type UICancelPaymentNotice,
    type UICancelPaymentStatus,
    type UICancelSummaryRow,
} from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Icon } from "../../atoms/icons/Icon"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { CancelSummaryCard } from "../../molecules/CancelSummaryCard"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { CancelPaymentMethod } from "../../organisms/CancelPaymentMethod"
import { CancelFlowStep } from "../../templates/CancelFlowStep"

const LEAD = "Le paiement des frais valide votre résiliation."
const OTHER_CARD_LABEL = "Utiliser une autre carte"
const BACK_LABEL = "Retour"
const SECURE_NOTE = "Paiement sécurisé par Stripe · authentification 3D Secure si demandée"
const CONFIRM_TITLE = "Confirmer la résiliation ?"
const CONFIRM_LABEL = "Confirmer la résiliation"

export type CancelPaymentPageProps = {
    /** Le montant à régler, déjà formaté — « 59,70 € ». */
    amountLabel: string
    /** Ce qui reste accessible et jusqu'à quand — déjà daté. */
    accessText: string
    /** Motif, fin d'abonnement, frais, à régler maintenant. */
    summaryRows: UICancelSummaryRow[]
    /** `null` → l'étape montre l'alerte et ses deux gestes. */
    paymentMethod: UICancelPaymentMethod | null
    /** Où en est le prélèvement — défaut `idle`. */
    paymentStatus?: UICancelPaymentStatus
    /** Ce que la banque ou le backend répond — 3D Secure en attente, ou refus. */
    paymentNotice?: UICancelPaymentNotice | null
    /** Demande le prélèvement — l'appelant ouvre la confirmation. */
    onPay: () => void
    /** Ouvre le portail de moyens de paiement. Absent → le lien disparaît. */
    onUseAnotherCard?: () => void
    onUpdateCard?: () => void
    onReloadPaymentMethod?: () => void
    reloading?: boolean
    // ── Confirmation — ouverte et fermée par l'appelant.
    confirmVisible?: boolean
    confirmMessage?: string
    onConfirm?: () => void
    onCancelConfirm?: () => void
    onBack?: () => void
    onClose?: () => void
    testID?: string
}

export function CancelPaymentPage(props: CancelPaymentPageProps) {
    const {
        amountLabel,
        accessText,
        summaryRows,
        paymentMethod,
        paymentStatus = "idle",
        paymentNotice,
        onPay,
        onUseAnotherCard,
        onUpdateCard,
        onReloadPaymentMethod,
        reloading = false,
        confirmVisible = false,
        confirmMessage,
        onConfirm,
        onCancelConfirm,
        onBack,
        onClose,
        testID,
    } = props

    const inFlight = paymentStatus === "submitting" || paymentStatus === "authenticating"
    const hasPaymentMethod = paymentMethod != null

    return (
        <CancelFlowStep testID={testID} stepTitle={`Régler ${amountLabel}`} step={4} onBack={onBack} onClose={onClose}>
            <Stack gap="4">
                <Text size="text-base" color="text-muted">
                    {`${LEAD} ${accessText}`}
                </Text>

                <CancelSummaryCard rows={summaryRows} />

                <CancelPaymentMethod
                    paymentMethod={paymentMethod}
                    onUpdateCard={onUpdateCard}
                    onReload={onReloadPaymentMethod}
                    reloading={reloading}
                />

                {/* Le lien doublerait « Mettre à jour ma carte » quand il n'y a pas de carte : même geste, même page. */}
                {hasPaymentMethod && onUseAnotherCard != null && (
                    <TextButton label={OTHER_CARD_LABEL} size="sm" fullWidth onPress={onUseAnotherCard} />
                )}

                {paymentNotice != null && (paymentStatus === "declined" || paymentStatus === "authenticating") && (
                    <AlertCard
                        severity={paymentStatus === "declined" ? "alert" : "info"}
                        message={
                            <Stack gap="1">
                                <Text size="text-md" weight="bold">
                                    {paymentNotice.title}
                                </Text>
                                <Text size="text-sm" color="text-primary">
                                    {paymentNotice.message}
                                </Text>
                            </Stack>
                        }
                    />
                )}

                <Button
                    label={`Payer ${amountLabel} et résilier`}
                    fullWidth
                    disabled={!hasPaymentMethod}
                    loading={inFlight}
                    onPress={onPay}
                />

                {onBack != null && <Button label={BACK_LABEL} variant="outline" fullWidth onPress={onBack} />}

                {/* Le cadenas remplace l'émoji du legacy ; le texte ne se centre pas dans sa boîte,
                    sinon il s'éloigne du cadenas dès qu'il passe à la ligne. */}
                <Row gap="2" justify="center" align="center">
                    <Icon name="lock" size={16} color="text-muted" />
                    <Text size="text-sm" color="text-muted" style={styles.secureNote}>
                        {SECURE_NOTE}
                    </Text>
                </Row>
            </Stack>

            {onConfirm != null && onCancelConfirm != null && (
                <ConfirmDialog
                    visible={confirmVisible}
                    title={CONFIRM_TITLE}
                    message={confirmMessage}
                    confirmLabel={CONFIRM_LABEL}
                    onConfirm={onConfirm}
                    onCancel={onCancelConfirm}
                />
            )}
        </CancelFlowStep>
    )
}

const styles = StyleSheet.create(() => ({
    secureNote: { flexShrink: 1 },
}))
